import { isEqual, isNil } from "lodash";

import {
  NO_GROUPING_QS_VALUE,
  UNRESOLVED_GRID_FIELD_PREFIX,
} from "../constants";
import {
  pageSchemaPropNames,
  type GridGrouping,
  type IFilterSchemaObject,
  type IPageSchema,
  type RydeUiUnit,
  type TFinalSchemaInitState,
  type TValidFinalSchema,
} from "../models";
import { hasKeys } from "./objects.helpers";
import type {
  ColumnOrderState,
  ColumnSizingState,
  ColumnSort,
  GroupingState,
  SortingState,
  VisibilityState,
} from "./react-table.features";
import { parseBoolean } from "./string.helpers";
import {
  getConstInitialFiltersByEntity,
  transformFiltersIdsToStoreObj,
} from "./table.filters.helpers";

export const checkIsReadyToInitFinalSchema = (
  state: TFinalSchemaInitState,
): boolean =>
  state.isViewSet &&
  state.isRootSchemaSet &&
  state.isProfileScopeSet &&
  !state.isFinalSchemaSet;

export const checkIsReadyToInitActiveView = (
  state: TFinalSchemaInitState,
): boolean =>
  state.isViewSet &&
  state.isRootSchemaSet &&
  state.isProfileScopeSet &&
  state.isFinalSchemaSet &&
  !state.isActiveViewResolved;

export const checkIsAllInit = (state: TFinalSchemaInitState): boolean =>
  Object.values(state).every(Boolean);

// TODO implement
export const isFinalSchemaValid = (
  localStoredFinalSchema: IPageSchema | null,
): localStoredFinalSchema is TValidFinalSchema => {
  if (isNil(localStoredFinalSchema)) {
    return false;
  }

  const isAllKeys = Object.keys(localStoredFinalSchema).every(key =>
    pageSchemaPropNames.includes(key),
  );

  return isAllKeys;
};

interface IResolveQSFilterSchemaArguments {
  filtersSchemaFromQs: IFilterSchemaObject;
  localStoredFinalSchemaFiltersSchema?: IFilterSchemaObject;
  viewFilterSchema?: IFilterSchemaObject;
}

const resolveQSFiltersSchema = ({
  filtersSchemaFromQs,
  localStoredFinalSchemaFiltersSchema,
  viewFilterSchema,
}: IResolveQSFilterSchemaArguments): IFilterSchemaObject => {
  const result = filtersSchemaFromQs;

  if (localStoredFinalSchemaFiltersSchema) {
    return { ...localStoredFinalSchemaFiltersSchema, ...result };
  }

  if (viewFilterSchema) {
    return { ...viewFilterSchema, ...result };
  }

  return result;
};

interface IResolveFilterSchemaArguments {
  isGlobalSearchActive?: boolean;
  filtersSchemaFromQs?: IFilterSchemaObject;
  localStoredFinalSchemaFiltersSchema?: IFilterSchemaObject;
  viewFilterSchema?: IFilterSchemaObject;
  defaultFilters?: number[];
  entity: RydeUiUnit;
}

export const resolveFilterSchema = ({
  isGlobalSearchActive,
  filtersSchemaFromQs,
  localStoredFinalSchemaFiltersSchema,
  viewFilterSchema,
  defaultFilters,
  entity,
}: IResolveFilterSchemaArguments): IFilterSchemaObject => {
  // empty filter initialise according to the global search filtering business logic
  if (isGlobalSearchActive) {
    return {};
  }

  if (filtersSchemaFromQs) {
    return resolveQSFiltersSchema({
      filtersSchemaFromQs,
      localStoredFinalSchemaFiltersSchema,
      viewFilterSchema,
    });
  }

  if (localStoredFinalSchemaFiltersSchema) {
    return localStoredFinalSchemaFiltersSchema;
  }

  if (viewFilterSchema) {
    return viewFilterSchema;
  }

  if (defaultFilters) {
    const schemaFilters = transformFiltersIdsToStoreObj(defaultFilters);

    if (schemaFilters) {
      return schemaFilters;
    }
  }

  const filters = getConstInitialFiltersByEntity(entity);
  return filters;
};

interface IResolveColumnsOrderArguments {
  viewColumnsOrder?: string[];
  columnsOrder?: number[];
  getGridFieldNameByControlId: (id: number) => string;
}

export const resolveColumnsOrder = ({
  viewColumnsOrder,
  columnsOrder,
  getGridFieldNameByControlId,
}: IResolveColumnsOrderArguments): ColumnOrderState => {
  if (viewColumnsOrder) {
    return viewColumnsOrder;
  }

  if (columnsOrder?.length) {
    return columnsOrder?.map(columnId => getGridFieldNameByControlId(columnId));
  }

  return [];
};

interface IResolveColumnsVisibilityArguments {
  viewColumnsVisibility?: VisibilityState;
  columnsVisibility?: Record<number, boolean>;
  getGridFieldNameByControlId: (id: number) => string;
}

export const resolveColumnsVisibility = ({
  viewColumnsVisibility,
  columnsVisibility,
  getGridFieldNameByControlId,
}: IResolveColumnsVisibilityArguments): VisibilityState => {
  if (viewColumnsVisibility && hasKeys(viewColumnsVisibility)) {
    return viewColumnsVisibility;
  }

  const result: Record<string, boolean> = {};

  Object.entries(columnsVisibility ?? {}).forEach(([key, val]) => {
    const columnIdComputed = getGridFieldNameByControlId(Number(key));

    result[columnIdComputed] = parseBoolean(val);
  });

  return result;
};

/** A view saved before widths were stored has no `columnsSizing` and leaves the widths free, so it never differs. */
export const isColumnsSizingDiffFromView = ({
  columnsSizing,
  viewColumnsSizing,
}: {
  columnsSizing?: ColumnSizingState;
  viewColumnsSizing?: ColumnSizingState;
}): boolean =>
  viewColumnsSizing !== undefined &&
  !isEqual(columnsSizing ?? {}, viewColumnsSizing);

interface IResolveColumnsSortingArguments {
  tableSortingFromQS?: ColumnSort[];
  localStoredFinalSchemaColumnsSorting?: SortingState;
  viewColumnsSorting?: ColumnSort[];
  sortBy?: ColumnSort[];
  getGridFieldNameByControlId: (id: number) => string;
}

export const resolveColumnsSorting = ({
  tableSortingFromQS,
  localStoredFinalSchemaColumnsSorting,
  viewColumnsSorting,
  sortBy,
  getGridFieldNameByControlId,
}: IResolveColumnsSortingArguments): SortingState => {
  if (tableSortingFromQS) {
    return tableSortingFromQS;
  }

  if (localStoredFinalSchemaColumnsSorting) {
    return localStoredFinalSchemaColumnsSorting;
  }

  if (viewColumnsSorting) {
    return viewColumnsSorting;
  }

  return (
    sortBy
      ?.map(columnSort => {
        const id = getGridFieldNameByControlId(Number(columnSort.id));
        return id ? { id, desc: columnSort.desc ?? false } : null;
      })
      .filter((item): item is ColumnSort => item !== null) ?? []
  );
};

interface IResolveColumnsGroupingArguments {
  localStoredFinalSchemaColumnsGrouping?: GroupingState;
  tableGroupingFromQS?: (string | null)[];
  viewColumnsGrouping?: string[];
  groupBy?: GridGrouping[];
  getGridFieldNameByControlId: (id: number) => string;
}

export const resolveColumnsGrouping = ({
  tableGroupingFromQS,
  viewColumnsGrouping,
  groupBy,
  getGridFieldNameByControlId,
}: IResolveColumnsGroupingArguments): GroupingState => {
  if (tableGroupingFromQS) {
    if (tableGroupingFromQS.at(0) === NO_GROUPING_QS_VALUE) {
      return [];
    }

    // Drop nulls and unresolved placeholders so a stale `group=unresolved_*` URL self-heals to "no grouping".
    return tableGroupingFromQS.filter(
      (x): x is string =>
        x !== null && !x.startsWith(UNRESOLVED_GRID_FIELD_PREFIX),
    );
  }

  if (viewColumnsGrouping) {
    return viewColumnsGrouping;
  }

  const groupById = Number(groupBy?.at(0)?.id);

  if (!Number.isFinite(groupById)) {
    return [];
  }

  const groupingField = getGridFieldNameByControlId(groupById);

  return groupingField &&
    !groupingField.startsWith(UNRESOLVED_GRID_FIELD_PREFIX)
    ? [groupingField]
    : [];
};
